import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect, useState } from 'react';
import properties from '../constants/properties';
import MainStore from '../stores/MainStore';
import { downloadLetterOfAuthority, downloadUboDeclaration, findStepData } from '../Utils/UkPrefill';
import { DOCUMENT_COLORS, documentButtonStyle, pillStyle, readUploadFile } from './UkDocumentUpload';

const NOT_DIRECTOR = 'No';

const isUploaded = (container, key) => Boolean(container?.[key] || container?.[`${key}_s3`]);

const fullName = (person) => [person.firstName, person.lastName].filter(Boolean).join(' ');

const OutstandingDocuments = observer(() => {
  const mainStore = useContext(MainStore);
  const [error, setError] = useState('');
  const [downloadError, setDownloadError] = useState('');

  const company = mainStore.creditApplicationDomain;

  const saveStepDocument = async (rootKey, mutate) => {
    setError('');
    const { stepIndex, data } = findStepData(mainStore, rootKey);
    mutate(data);
    try {
      await mainStore.saveStepFormData(stepIndex, data);
    } catch (e) {
      setError('The document could not be saved. Please try again.');
    }
  };

  const setValue = (container, key, value) => {
    if (value) {
      container[key] = value;
    } else {
      delete container[key];
      delete container[`${key}_s3`];
    }
  };

  const download = (downloader, label) => async () => {
    setDownloadError('');
    try {
      await downloader();
    } catch (e) {
      setDownloadError(`The ${label} could not be downloaded. Please try again.`);
    }
  };

  const prefillButton = (downloader, label) => (
    <button
      type="button"
      style={documentButtonStyle(DOCUMENT_COLORS.orange, DOCUMENT_COLORS.orange)}
      onClick={download(downloader, label)}
    >
      Pre-filled PDF
    </button>
  );

  const documents = [];

  documents.push({
    id: 'confirm_companiesHouseExtract',
    title: 'Companies House Extract / Certificate of Incorporation',
    description: 'Dated within 3 months. Company status is confirmed against the Companies House register.',
    value: company.companiesHouseExtractBase64,
    onChange: (value) => {
      mainStore.updateCompanyInfo('companiesHouseExtractBase64', value);
      mainStore.saveCreditApplication(false);
    },
  });

  const address = findStepData(mainStore, 'addressDocuments').data.addressDocuments;
  documents.push({
    id: 'confirm_utilityBill',
    title: 'Proof of Registered Office Address (utility bill, rates notice or lease, within 3 months)',
    description: 'Dual-evidence address verification against the Companies House registered office.',
    value: address?.file_utilityBill,
    uploaded: isUploaded(address, 'file_utilityBill'),
    onChange: (value) => saveStepDocument('addressDocuments', (data) => setValue(data.addressDocuments, 'file_utilityBill', value)),
  });

  (findStepData(mainStore, 'mainContacts').data.mainContacts || []).forEach((contact, index) => {
    if (!String(contact.isDirectorOrOfficer || '').startsWith(NOT_DIRECTOR)) return;

    documents.push({
      id: `confirm_loa_${index}`,
      title: `Letter of Authority (signed by a director) - ${contact.fullName || `contact ${index + 1}`}`,
      description: 'The main contact is not a director/officer - a signed Letter of Authority must evidence their authority to act.',
      value: contact.file_letterOfAuthority,
      uploaded: isUploaded(contact, 'file_letterOfAuthority'),
      extraAction: prefillButton(() => downloadLetterOfAuthority(mainStore, index), 'Letter of Authority'),
      onChange: (value) => saveStepDocument('mainContacts', (data) => setValue(data.mainContacts[index], 'file_letterOfAuthority', value)),
    });
  });

  (findStepData(mainStore, 'directorsOrOwners').data.directorsOrOwners || []).forEach((person, index) => {
    [
      ['file_directorDocumentFront', 'Front'],
      ['file_directorDocumentBack', 'Back'],
    ].forEach(([key, side]) => {
      documents.push({
        id: `confirm_${key}_${index}`,
        title: `Attach Passport or Driver's License - ${side} - ${fullName(person) || `person ${index + 1}`}`,
        description:
          "Please provide a clear & coloured copy of your driver's license (front & back) or passport. Kindly ensure that important details on the ID are not cropped",
        value: person[key],
        uploaded: isUploaded(person, key),
        onChange: (value) => saveStepDocument('directorsOrOwners', (data) => setValue(data.directorsOrOwners[index], key, value)),
      });
    });
  });

  const ubo = findStepData(mainStore, 'peopleDocuments').data.peopleDocuments;
  documents.push({
    id: 'confirm_uboDeclaration',
    title: 'Beneficial Ownership (UBO) Declaration',
    description: 'Signed declaration of every individual owning 25% or more or otherwise exercising control. The PSC register alone does not satisfy CDD (MLR 2017 reg 28(9)).',
    value: ubo?.file_uboDeclaration,
    uploaded: isUploaded(ubo, 'file_uboDeclaration'),
    extraAction: prefillButton(() => downloadUboDeclaration(mainStore), 'UBO Declaration'),
    onChange: (value) => saveStepDocument('peopleDocuments', (data) => setValue(data.peopleDocuments, 'file_uboDeclaration', value)),
  });

  const isDone = (doc) => (doc.uploaded !== undefined ? doc.uploaded : Boolean(doc.value));
  const outstanding = documents.filter((doc) => !isDone(doc));
  const outstandingTitles = outstanding.map((doc) => doc.title);
  const outstandingKey = outstandingTitles.join('|');

  useEffect(() => {
    mainStore.setOutstandingDocumentTitles(outstandingTitles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outstandingKey]);

  useEffect(() => () => mainStore.setOutstandingDocumentTitles([]), [mainStore]);

  const upload = (doc) => async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) return;

    setError('');
    const { value, error: fileError } = await readUploadFile(file);
    if (fileError) {
      setError(fileError);
      return;
    }
    doc.onChange(value);
  };

  const bannerStyle = outstanding.length
    ? { background: DOCUMENT_COLORS.warnBg, border: '1px solid #f2b8c0', color: '#7a0a1e' }
    : { background: DOCUMENT_COLORS.okBg, border: '1px solid #bfe0bf', color: '#1d5a20' };

  return (
    <div className="px-10 pt-8 pb-8">
      <div style={{ ...bannerStyle, borderRadius: '8px', padding: '12px 16px', fontSize: '12.5px' }}>
        {outstanding.length ? (
          <>
            <b>{`${outstanding.length} required document${outstanding.length > 1 ? 's' : ''} outstanding.`}</b> Upload{' '}
            {outstanding.length > 1 ? 'them' : 'it'} below, or submit now and provide{' '}
            {outstanding.length > 1 ? 'them' : 'it'} afterwards by email.
          </>
        ) : (
          <>
            <b>All required documents uploaded.</b> The application is ready to submit.
          </>
        )}
      </div>
      {outstanding.length > 0 && (
        <div style={{ border: `1px solid ${DOCUMENT_COLORS.line}`, borderRadius: '8px', overflow: 'hidden', margin: '4px 0 8px' }}>
          {outstanding.map((doc, index) => (
            <div
              key={doc.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '10px 14px',
                background: '#fffdfa',
                borderBottom: index === outstanding.length - 1 ? 'none' : `1px solid ${DOCUMENT_COLORS.line}`,
              }}
            >
              <span style={pillStyle(DOCUMENT_COLORS.warn, DOCUMENT_COLORS.warnBg)}>REQUIRED</span>
              <span style={{ flex: 1, fontSize: '12.5px', color: DOCUMENT_COLORS.text }}>{doc.title}</span>
              {doc.extraAction}
              <label htmlFor={doc.id} style={documentButtonStyle()}>
                Upload file
              </label>
              <input
                id={doc.id}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={upload(doc)}
              />
            </div>
          ))}
        </div>
      )}
      {(error || downloadError) && (
        <span style={{ display: 'block', marginTop: '8px', fontSize: '12px', color: DOCUMENT_COLORS.warn }}>
          {error || downloadError}
        </span>
      )}
    </div>
  );
});

const UkOutstandingDocuments = observer(() => {
  const mainStore = useContext(MainStore);

  if (!properties.UK_ONBOARDING_ORGS.includes(mainStore.creditApplicationDomain.organizationId)) {
    return null;
  }

  return <OutstandingDocuments />;
});

export default UkOutstandingDocuments;
